import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/redux/store'
import { getRoomDetails, resetGame } from '@/redux/asyncThunks/room'
import { GameRoomStatus, Room } from '@/redux/types/room'
import {
  endCurrentPhaseThunk,
  endCurrentRoundThunk,
  endGame,
  endSimulationThunk,
  fetchTimerStatusThunk,
  pauseGameThunk,
  previousPhaseThunk,
  resumeGameThunk,
  startNextRoundThunk,
} from '@/redux/asyncThunks/timerConfiguration'
import { toast } from 'sonner'
import { disconnectNegotiationWebSocket } from '@/redux/asyncThunks/negotiationWebsocket'
import { disconnectAdvisorWebSocket } from '@/redux/asyncThunks/websocketThunks'
import { disconnectDecisionWebSocket } from '@/redux/asyncThunks/studentDecisionWebsocket'
import { disconnectTeamChatWebSocket } from '@/redux/asyncThunks/teamChat'
import {
  clearMessages,
  setActiveAdvisor,
  setActiveProfessor,
  setActiveTab,
  setInput,
} from '@/redux/slices/chatSlice'
import { clearMessages as clearTeamChatMessages } from '@/redux/slices/teamChatSlice'
import { disconnectTimerWebSocket } from '@/redux/asyncThunks/timerWebsocket'
import { ActiveTab } from '@/constants/chat'
import { ActivityState } from '@/redux/slices/timerConfigurationSlice'

export const useRoomControls = (
  roomId: string,
  phaseId?: number | null,
  roundId?: number | null,
  roundStatus?: ActivityState | null
) => {
  const dispatch = useDispatch<AppDispatch>()
  const [isEndGameModalOpen, setIsEndGameModalOpen] = useState(false)
  const [isEndingGame, setIsEndingGame] = useState(false)
  const [isEndSimulationModalOpen, setIsEndSimulationModalOpen] =
    useState(false)
  const [isEndingSimulation, setIsEndingSimulation] = useState(false)
  const [isResetModalOpen, setIsResetModalOpen] = useState(false)
  const [controlsLoading, setControlsLoading] = useState<{
    startNextRound: boolean
    endRound: boolean
    endPhase: boolean
    previousPhase: boolean
    pauseGame: boolean
  }>({
    startNextRound: false,
    endRound: false,
    endPhase: false,
    previousPhase: false,
    pauseGame: false,
  })
  const [timeControlsEnabled, setTimeControlsEnabled] = useState<boolean>(true)

  const handleResetGame = useCallback(
    async (currentRoom: Room) => {
      if (!currentRoom?.roomid) return

      try {
        await dispatch(resetGame(currentRoom.roomid)).unwrap()

        // Clear all chat messages
        dispatch(clearMessages())
        dispatch(clearTeamChatMessages())

        // Disconnect all websockets
        dispatch(disconnectTimerWebSocket())
        dispatch(disconnectNegotiationWebSocket())
        dispatch(disconnectAdvisorWebSocket())
        dispatch(disconnectDecisionWebSocket())
        dispatch(disconnectTeamChatWebSocket())

        // Reset chat state
        dispatch(setActiveTab(ActiveTab.Advisor))
        dispatch(setActiveAdvisor([]))
        dispatch(setActiveProfessor(false))
        dispatch(setInput(''))

        await dispatch(getRoomDetails(roomId)).unwrap()
      } catch (error) {
        console.error('Failed to reset game:', error)
        toast.error('Failed to reset game. Please try again.')
      } finally {
        setIsResetModalOpen(false)
      }
    },
    [dispatch, roomId]
  )

  const handleResetClick = useCallback((currentRoom: Room) => {
    if (currentRoom?.status === GameRoomStatus.NOT_STARTED) {
      toast.error("Cannot reset a game that hasn't started yet")
      return
    }
    setIsResetModalOpen(true)
  }, [])

  const handleEndGame = useCallback(
    async (currentRoom: Room) => {
      if (!currentRoom?.roomid) return

      setIsEndingGame(true)
      try {
        dispatch(endGame())
        dispatch(getRoomDetails(roomId))
        setIsEndGameModalOpen(false)
        toast.info('You will be notified when the summary is available')
      } catch (error) {
        console.error('Failed to end game:', error)
      } finally {
        setIsEndingGame(false)
      }
    },
    [dispatch, roomId]
  )

  const handleEndSimulation = useCallback(
    async (currentRoom: Room) => {
      if (!currentRoom?.roomid) return

      setIsEndingSimulation(true)
      try {
        await dispatch(endSimulationThunk()).unwrap()

        setIsEndSimulationModalOpen(false)
        toast.success('Pilot ended successfully. All pilot data has been deleted.')
      } catch (error) {
        console.error('Failed to end pilot:', error)
        toast.error('Failed to end pilot. Please try again.')
      } finally {
        setIsEndingSimulation(false)
      }
    },
    [dispatch, roomId]
  )

  const handleStartNextRound = useCallback(
    (isGameCompleted: boolean) => {
      if (isGameCompleted) return

      setControlsLoading((prev) => ({
        ...prev,
        startNextRound: true,
      }))

      dispatch(startNextRoundThunk(roomId))
        .unwrap()
        .then(() => {
          toast.success('Started next round')
          dispatch(fetchTimerStatusThunk(roomId))
          dispatch(getRoomDetails(roomId))
        })
        .catch((error) => {
          toast.error(`Failed to start next round: ${error}`)
        })
        .finally(() => {
          setControlsLoading((prev) => ({
            ...prev,
            startNextRound: false,
          }))
        })
    },
    [dispatch, roomId]
  )

  const handleEndRound = useCallback(
    (isGameCompleted: boolean) => {
      if (isGameCompleted) return

      setControlsLoading((prev) => ({
        ...prev,
        endRound: true,
      }))

      if (roundId && roundId !== null) {
        dispatch(endCurrentRoundThunk(roundId))
          .unwrap()
          .then(() => {
            toast.success('Ended current round')
            dispatch(fetchTimerStatusThunk(roomId))
            dispatch(getRoomDetails(roomId))
          })
          .catch((error) => {
            toast.error(`Failed to end round: ${error}`)
          })
          .finally(() => {
            setControlsLoading((prev) => ({
              ...prev,
              endRound: false,
            }))
          })
      }
    },
    [dispatch, roomId, roundId]
  )

  const handleEndPhase = useCallback(
    (isGameCompleted: boolean) => {
      if (isGameCompleted) return

      setControlsLoading((prev) => ({
        ...prev,
        endPhase: true,
      }))

      if (phaseId && phaseId !== null) {
        dispatch(endCurrentPhaseThunk(phaseId))
          .unwrap()
          .then(() => {
            toast.success('Ended current phase')
            dispatch(fetchTimerStatusThunk(roomId))
            dispatch(getRoomDetails(roomId))
          })
          .catch((error) => {
            toast.error(`Failed to end phase: ${error}`)
          })
          .finally(() => {
            setControlsLoading((prev) => ({
              ...prev,
              endPhase: false,
            }))
          })
      }
    },
    [dispatch, roomId, phaseId]
  )

  const handlePauseGame = useCallback(
    (isGameCompleted: boolean) => {
      if (isGameCompleted) return

      if (roundStatus !== ActivityState.Paused) {
        setControlsLoading((prev) => ({
          ...prev,
          pauseGame: true,
        }))

        dispatch(pauseGameThunk())
          .unwrap()
          .then(() => {
            // Success toast comes from the server-confirmed timer_control_result
            // (timerProcessors) — toasting here too doubled it on one click.
            dispatch(fetchTimerStatusThunk(roomId))
          })
          .catch((error) => {
            toast.error(`Failed to pause the game: ${error}`)
          })
          .finally(() => {
            setControlsLoading((prev) => ({
              ...prev,
              pauseGame: false,
            }))
          })
      } else {
        setControlsLoading((prev) => ({
          ...prev,
          pauseGame: true,
        }))

        dispatch(resumeGameThunk())
          .unwrap()
          .then(() => {
            // Success toast comes from the server-confirmed timer_control_result.
            dispatch(fetchTimerStatusThunk(roomId))
          })
          .catch((error) => {
            toast.error(`Failed to resume the game: ${error}`)
          })
          .finally(() => {
            setControlsLoading((prev) => ({
              ...prev,
              pauseGame: false,
            }))
          })
      }
    },
    [dispatch, roomId, roundStatus]
  )

  const handlePreviousPhase = useCallback(
    (isGameCompleted: boolean) => {
      if (isGameCompleted) return

      setControlsLoading((prev) => ({
        ...prev,
        previousPhase: true,
      }))

      dispatch(previousPhaseThunk(roomId))
        .unwrap()
        .then(() => {
          toast.success('Moved to previous phase')
          dispatch(fetchTimerStatusThunk(roomId))
          dispatch(getRoomDetails(roomId))
        })
        .catch((error) => {
          toast.error(`Failed to move to previous phase: ${error}`)
        })
        .finally(() => {
          setControlsLoading((prev) => ({
            ...prev,
            previousPhase: false,
          }))
        })
    },
    [dispatch, roomId]
  )

  return {
    isEndGameModalOpen,
    setIsEndGameModalOpen,
    isEndingGame,
    isEndSimulationModalOpen,
    setIsEndSimulationModalOpen,
    isEndingSimulation,
    isResetModalOpen,
    setIsResetModalOpen,
    controlsLoading,
    timeControlsEnabled,
    setTimeControlsEnabled,
    handleResetGame,
    handleResetClick,
    handleEndGame,
    handleEndSimulation,
    handleStartNextRound,
    handleEndRound,
    handleEndPhase,
    handlePreviousPhase,
    handlePauseGame,
  }
}
